<template>
  <article class="product-card" :class="{ 'product-card--light': tone === 'light' }">
    <router-link
      class="product-card__media"
      :to="productRoute"
      :aria-label="`Ver ${product.name}`"
    >
      <OptimizedImage
        :src="selectedBlister?.image || product.image || fallbackLogo"
        :alt="product.name"
        :width="480"
        :height="480"
        :max-width="640"
        :widths="[240, 320, 400, 480, 640]"
        sizes="(max-width: 520px) 100vw, (max-width: 900px) 50vw, 33vw"
        fit="cover"
        skip-dpr
        cloudinary-crop="scale"
        class="product-card__image"
        :class="{ 'product-card__image--loading': !imageLoaded }"
        @load="handleImageLoad"
      />

      <span
        v-if="showDiscount"
        class="product-card__badge"
      >
        −{{ formatDiscount(selectedBlister.discount) }}%
      </span>
    </router-link>

    <div class="product-card__body">
      <p v-if="product.brand" class="product-card__brand">{{ product.brand }}</p>

      <router-link class="product-card__title-link" :to="productRoute">
        <h3 class="product-card__title">{{ product.name }}</h3>
      </router-link>

      <p v-if="product.type === 'common' ? merchSummary : product.genotype" class="product-card__genotype">
        {{ product.type === 'common' ? merchSummary : product.genotype }}
      </p>

      <div class="product-card__footer">
        <span v-if="product.type" class="product-card__type">
          {{ formatType(product.type, product.category) }}
        </span>
        <router-link
          class="product-card__link"
          :to="productRoute"
          :aria-label="`Ver detalles de ${product.name}`"
        >
          {{ product.type === 'common' ? 'Ver producto' : 'Ver genética' }}
          <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
            <path d="M4 10h11M11 6l4 4-4 4" />
          </svg>
        </router-link>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, ref, watch, defineProps } from 'vue'
import { isAuthenticated } from '@/services/auth'
import { getFirstAvailableBlister } from '@/helpers/blisterUtils'
import OptimizedImage from '@/components/common/OptimizedImage.vue'
import fallbackLogo from '@/assets/images/logo.png'

const props = defineProps({
  tone: {
    type: String,
    default: 'dark'
  },
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      brand_id: '',
      brand: '',
      name: '',
      image: '',
      image_2: null,
      image_3: null,
      description: '',
      blisters: [],
      flowering_time: 0,
      thc: '',
      cbd: '',
      flavors: '',
      type: '',
      is_national: false
    })
  }
})

const imageLoaded = ref(false)
const selectedBlister = ref(null)
const userAuthenticated = isAuthenticated()

const productRoute = computed(() => ({
  name: 'ProductDetails',
  params: { id: props.product.id }
}))

const showDiscount = computed(() => (
  userAuthenticated &&
  selectedBlister.value &&
  Number(selectedBlister.value.discount) > 0
))

const merchSummary = computed(() => {
  const sizes = [...new Set((props.product.blisters || []).map(blister => blister.option_values?.Talle).filter(Boolean))]
  if (sizes.length) return `Talles: ${sizes.join(', ')}`
  const colors = [...new Set((props.product.blisters || []).map(blister => blister.option_values?.Color).filter(Boolean))]
  if (colors.length) return `Colores: ${colors.join(', ')}`
  return props.product.product_size ? `Talle / tamaño: ${props.product.product_size}` : ''
})

watch(() => props.product, (newProduct) => {
  imageLoaded.value = false
  selectedBlister.value = Array.isArray(newProduct?.blisters) && newProduct.blisters.length
    ? getFirstAvailableBlister(newProduct.blisters)
    : null
}, { immediate: true })

const handleImageLoad = () => {
  imageLoaded.value = true
}

const formatDiscount = (discount) => Math.round(Number(discount) || 0)

const formatType = (type, category) => {
  if (type === 'common') return category === 'merch' ? 'Merch' : 'Producto común'
  const typeLabels = {
    auto: 'Automática',
    automatic: 'Automática',
    fem: 'Feminizada',
    feminized: 'Feminizada',
    reg: 'Regular',
    regular: 'Regular'
  }

  return typeLabels[String(type).toLowerCase()] || type
}
</script>

<style scoped>
.product-card {
  position: relative;
  display: flex;
  min-width: 0;
  height: 100%;
  flex-direction: column;
  color: #fff;
  
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.product-card__media {
  position: relative;
  display: block;
  aspect-ratio: 1;
  overflow: hidden;
  background: #0a0a0a;
  color: inherit;
}

.product-card__media:focus-visible,
.product-card__title-link:focus-visible,
.product-card__link:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 4px;
}

.product-card__image {
  width: 100%;
  height: 100%;
  object-position: left top;
  opacity: 1;
  transform: scale(1);
  transition:
    opacity var(--duration-fast, 260ms) var(--ease-out, ease-out),
    transform var(--duration-slow, 520ms) var(--ease-smooth-out, cubic-bezier(0.16, 1, 0.3, 1));
}

.product-card__image--loading {
  opacity: 0;
}

.product-card__badge {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.42rem 0.56rem;
  background: #fff;
  color: #000;
  font-size: 0.68rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  line-height: 1;
}

.product-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1rem 0 0.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.75rem;
}

.product-card__brand,
.product-card__genotype {
  margin: 0;
}

.product-card__brand {
  margin-bottom: 0.4rem;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  line-height: 1.25;
  text-transform: uppercase;
}

.product-card__title-link {
  color: inherit;
  text-decoration: none;
}

.product-card__title {
  margin: 0;
  font-size: clamp(1.02rem, 1.2vw, 1.24rem);
  font-weight: 520;
  letter-spacing: -0.02em;
  line-height: 1.12;
  text-wrap: balance;
}

.product-card__genotype {
  margin-top: 0.48rem;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.78rem;
  line-height: 1.45;
}

.product-card__footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  /* Si el tipo y "Ver genética" no entran (tarjetas de 2 columnas en mobile),
     el link baja de línea en lugar de comprimirse y salirse de la card. */
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  min-height: 2.5rem;
  margin-top: auto;
  padding-top: 1rem;
  padding-bottom: 0.78rem;
}

.product-card__type {
  min-width: 0;
  color: rgba(255, 255, 255, 0.42);
  font-size: 0.66rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.product-card__link {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  margin-left: auto;
  white-space: nowrap;
  gap: 0.4rem;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 650;
  letter-spacing: 0.07em;
  text-decoration: none;
  text-transform: uppercase;
}

.product-card__link svg {
  width: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: square;
  stroke-width: 1.4;
  transform: translateX(0);
  transition: transform var(--duration-micro, 160ms) var(--ease-out, ease-out);
}

.product-card--light {
  background: #fff;
  color: #000;
  border-color: rgba(0, 0, 0, 0.18);
}

.product-card--light .product-card__media {
  background: #fff;
}

.product-card--light .product-card__body {
  border-top-color: rgba(0, 0, 0, 0.18);
}

.product-card--light .product-card__title-link,
.product-card--light .product-card__title {
  color: #000;
}

.product-card--light .product-card__brand,
.product-card--light .product-card__genotype,
.product-card--light .product-card__type,
.product-card--light .product-card__link {
  color: #000;
}

.product-card--light .product-card__badge {
  outline: 1px solid #000;
}

.product-card--light .product-card__media:focus-visible,
.product-card--light .product-card__title-link:focus-visible,
.product-card--light .product-card__link:focus-visible {
  outline-color: #000;
}

@media (hover: hover) and (pointer: fine) {
  .product-card:hover .product-card__image {
    transform: scale(var(--scale-hover, 1.03));
  }

  .product-card__link:hover svg,
  .product-card__title-link:hover ~ .product-card__footer .product-card__link svg {
    transform: translateX(var(--distance-micro, 4px));
  }
}

@media (max-width: 520px) {
  .product-card__body {
    padding-top: 0.85rem;
  }

  .product-card__footer {
    align-items: center;
  }

  .product-card__link {
    min-height: 2.75rem;
    margin-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .product-card__image,
  .product-card__link svg {
    transition-duration: 0.01ms !important;
  }

  .product-card:hover .product-card__image,
  .product-card__link:hover svg {
    transform: none;
  }
}
</style>
